import type { ItemView } from "@fall26/shared";
import { QuickDate } from "./item/QuickDate.js";
import { CourseDot } from "./CourseDot.js";

/**
 * One item, one thumb.
 *
 * The checkbox is a 44px target and the row updates IN PLACE — it does not
 * reorder, animate away, or regroup. Regrouping happens on an explicit refresh
 * or a tab change, never underneath the thumb that just tapped.
 */
export function ItemRow({
  item,
  colorIndex,
  onToggle,
  onOpen,
  dense = false,
  quickDate = false,
}: {
  item: ItemView;
  colorIndex: number;
  onToggle: (done: boolean) => void;
  /** Opens the editor. The row body is the target; the checkbox is not. */
  onOpen?: () => void;
  /** Inside a day column, where the date is already the column header. */
  dense?: boolean;
  /** Offer the one-gesture date control instead of just the badge. */
  quickDate?: boolean;
}) {
  const needsDate = item.due.date === null || !item.due.confirmed;

  return (
    <li className="relative rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-2.5 pl-3.5">
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-[3px] rounded-l-[var(--radius)]"
        style={{ backgroundColor: `var(--course-${colorIndex % 6})` }}
      />

      <div className="flex items-start gap-2.5">
        <button
          type="button"
          role="checkbox"
          aria-checked={item.done}
          aria-label={`${item.done ? "Reopen" : "Complete"} ${item.title}`}
          onClick={() => onToggle(!item.done)}
          className="tap -my-1 -ml-1 flex items-center justify-center"
        >
          <span
            className={`flex h-5 w-5 items-center justify-center rounded-[5px] border-2 transition-colors ${
              item.done
                ? "border-[var(--done)] bg-[var(--done)]"
                : "border-[var(--border-strong)]"
            }`}
          >
            {item.done && (
              <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden>
                <path
                  d="M2.5 6.2 4.7 8.4 9.5 3.6"
                  fill="none"
                  stroke="var(--accent-contrast)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </span>
        </button>

        {/*
         * The row body opens the editor; the checkbox does not. Two targets, one
         * row: ticking something off is the gesture you make dozens of times a
         * day and must never open a sheet by accident, so the checkbox keeps its
         * own 44px box and the rest of the row is the way in.
         */}
        <button
          type="button"
          onClick={onOpen}
          disabled={!onOpen}
          aria-label={onOpen ? `Edit ${item.title}` : undefined}
          className="min-w-0 flex-1 text-left disabled:cursor-default"
        >
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <CourseDot colorIndex={colorIndex} />
            <span className="tabular text-[10px] uppercase tracking-[0.1em] text-[var(--text-subtle)]">
              {item.courseId}
            </span>
            {item.gate && (
              <span className="tabular rounded-full bg-[var(--overdue-wash)] px-1.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--overdue)]">
                gate
              </span>
            )}
            {/*
             * Kept after Canvas stopped carrying it — because I chose to keep
             * it. The badge is the reminder that this row is now mine alone and
             * no future sync will correct its date.
             */}
            {item.canvasStale && (
              <span className="tabular rounded-full bg-[var(--unknown-wash)] px-1.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--unknown)]">
                not in canvas
              </span>
            )}
            {item.kind === "exam" && (
              <span className="tabular rounded-full bg-[var(--accent-wash)] px-1.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--accent)]">
                exam
              </span>
            )}
            {!dense && !needsDate && (
              <span className="tabular ml-auto text-[11px] text-[var(--text-muted)]">
                {item.display.dateTime}
              </span>
            )}
            {dense && item.display.time && (
              <span className="tabular ml-auto text-[11px] text-[var(--text-muted)]">
                {item.display.time}
              </span>
            )}
          </div>

          <p
            className={`mt-0.5 text-[13.5px] leading-snug ${
              item.done
                ? "text-[var(--text-subtle)] line-through"
                : "text-[var(--text)]"
            }`}
          >
            {item.title}
          </p>

          {item.detail && (
            // In a day column the detail is scanning material, not reading
            // material — two lines, and the full text lives in Work. Left
            // unclamped in a list, where the row has the width for it.
            <p
              className={`tabular mt-0.5 text-[11.5px] leading-snug text-[var(--text-muted)] ${
                dense ? "line-clamp-2" : ""
              }`}
            >
              {item.detail}
            </p>
          )}

          {needsDate && !quickDate && (
            <span className="tabular mt-1 inline-block rounded-full bg-[var(--unknown-wash)] px-2 py-0.5 text-[10px] uppercase tracking-[0.06em] text-[var(--unknown)]">
              needs a date
            </span>
          )}
        </button>
      </div>

      {/* Below the row, not inside it: it carries its own inputs and buttons,
          which cannot be nested in the button that opens the editor. */}
      {needsDate && quickDate && (
        <div className="pl-[30px]">
          <QuickDate item={item} />
        </div>
      )}
    </li>
  );
}
